import type { EReasoningMode, IDownloadProgressEvent, IMessage } from "./chat";
import type { TEntityId } from "./entity-id";
import type { IPiiEntityTypeCount, IPiiSpan } from "./pii-span";

export interface IAnalyticsResponse {
  flaggedPercentage: {
    totalMessages: number;
    flaggedMessages: number;
    percentage: number;
  };
  categoryDistribution: Array<{
    category: string;
    count: number;
    percentage: number;
  }>;
  flaggedVsSafe: {
    flagged: number;
    safe: number;
    flaggedPercentage: number;
    safePercentage: number;
  };
  messagesByDirection: Array<{
    direction: string;
    flagged: number;
    total: number;
    percentage: number;
  }>;
  topUsers: Array<{
    userId: TEntityId;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    flaggedCount: number;
  }>;
  totalAnalyzedMessages: number;
  dateRange: {
    from: Date;
    to: Date;
  };
  dailyStats: Array<{
    date: string;
    totalMessages: number;
    flaggedMessages: number;
    safeMessages: number;
    flaggedPercentage: number;
    threatsFlagged: number;
  }>;
  dailyFlaggedVsSafe: Array<{
    date: string;
    flagged: number;
    safe: number;
  }>;
}

export interface IMessagesSummary {
  totalMessages: number;
  flaggedMessages: number;
  flaggedPercentage: number;
  lastAnalyzedAt: Date | null;
}

export enum EAnalyticsMessageSource {
  EXTERNAL_API = "external_api",
  // NOTE: NSAI-7030 — a probe verdict on an agent or workflow run.
  AUTOMATION = "automation",
  CHAT_UI = "chat_ui",
  ASSISTANT_PREVIEW = "assistant_preview",
  TELEGRAM = "telegram",
  TEAMS = "teams",
  SLACK = "slack",
  WHATSAPP = "whatsapp",
  MATRIX = "matrix",
}

// NOTE: NSAI-5707 — one turn's retrieval-screening outcome, aggregated across
// the knowledge-base and chat/assistant-file retrieval paths.
export interface IRetrievalScreening {
  // NOTE: How many chunks screening looked at. Carried alongside the drop count
  // because "2 of 8 removed" and "2 of 2 removed" say very different things
  // about whether a probe is over-matching.
  inspectedCount: number;
  droppedCount: number;
  drops: IRetrievalScreeningDrop[];
}

export interface IRetrievalScreeningDrop {
  // NOTE: The document the chunk came from. Null when the source could not be
  // labelled (a chunk with no file name in its metadata).
  source: string | null;
  // NOTE: Null on the two drop paths where no probe matched: a fail-closed drop
  // (screening could not run) and an overflow drop (more chunks than the rails
  // inspect in one call). The reason field says which.
  probeUuid: string | null;
  probeName: string | null;
  matchedBehavior: string | null;
  category: string | null;
  reason: string | null;
}

export interface IAnalyticsMessage {
  uuid: string;
  role: string;
  model: string | null;
  provider: string | null;
  direction: "input" | "output";
  flagged: boolean;
  flagCategory: string | null;
  flagDetails: string | null;
  // NOTE: NSAI-5707 — what RAG retrieval screening removed from this turn.
  // The flag fields above are the MESSAGE-level verdict; this is the
  // chunk-level one, and it had no administrator-facing surface at all before:
  // a probe stripping legitimate knowledge left only a Token Usage cost row, so
  // it looked identical to a knowledge base with no answer. Optional on the
  // wire (older fixtures/mocks predate it); null or absent means retrieval
  // screening removed nothing.
  retrievalScreening?: IRetrievalScreening | null;
  probeUuid: string | null;
  chatUuid: string;
  // NOTE: Used by the message-detail modal so the follow-up context fetch
  // targets the row's actual project (matters in org-wide scope where rows
  // can come from different projects than the ambient X-Project-Uuid).
  projectUuid: string;
  messageSource: EAnalyticsMessageSource;
  assistantName?: string | null;
  isAssistantDeleted?: boolean;
  // NOTE: True when the row's (single) model was soft-deleted. Optional on the
  // wire to keep older fixtures/mocks valid; the UI keeps showing the model
  // name with a "(deleted)" marker instead of collapsing to "-".
  isModelDeleted?: boolean;
  isMultiModel: boolean;
  modelNames: string | null;
  providerNames: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  userEmail: string | null;
  createdAt: string;
  // NOTE: Optional on the wire to keep older fixtures/mocks valid; backend
  // always emits this (null when the row is not deleted).
  deletedAt?: string | null;
  // NOTE: Only present when the message was sent using a reasoning-capable
  // model and the user had the reasoning mode selector visible. Undefined
  // means the model did not offer a reasoning mode choice.
  reasoningMode?: EReasoningMode | null;
}

export enum EAnalyticsScope {
  ORG = "org",
}

// NOTE: Optional scope override for the Messages-tab endpoints. When neither
// is set the request is scoped to the X-Project-Uuid header (today's
// behavior). projectUuid switches the analytics query to a different
// project; scope=org switches to org-wide and is owner-only on the server.
export interface IAnalyticsScopeParams {
  projectUuid?: string;
  scope?: EAnalyticsScope;
}

export interface IAnalyticsQueryParams extends IAnalyticsScopeParams {
  hasAllMembersAnalyticsReadPermission: boolean;
  dateFrom?: string;
  dateTo?: string;
  // NOTE: Empty array / undefined ⇒ no member filter. Only forwarded on the
  // all-members route, gated by ALL_MEMBERS_ANALYTICS_READ.
  memberUuids?: string[];
  includeDeleted?: boolean;
}

export interface IRecentFlaggedMessagesParams extends IAnalyticsQueryParams {
  limit?: number;
}

export enum EAnalyticsMessageSortColumn {
  CREATED_AT = "createdAt",
  DIRECTION = "direction",
  MODEL = "model",
  PROVIDER = "provider",
  FLAG_CATEGORY = "flagCategory",
}

export type TAnalyticsSortOrder = "ASC" | "DESC";

// NOTE: Messages-list endpoints use a tri-state `deletionStatus` enum instead
// of the binary `includeDeleted` shared by the other analytics endpoints.
// Omitting it means "both live and deleted" — today's default view.
export enum EDeletionStatus {
  LIVE = "live",
  DELETED = "deleted",
}

// NOTE: Category keys for the Security Analytics → Messages filter UI. Shared
// by web (FilterDropdown) and mobile (FilterBottomSheet) so the categories
// array, filterValues object, multi-setter map, and chip-dispatch switch all
// reference the same source of truth.
export enum EMessagesFilterKey {
  MEMBER = "member",
  DIRECTION = "direction",
  SOURCE = "source",
  STATUS = "status",
  DELETION_STATUS = "deletionStatus",
  MODEL = "model",
  PROVIDER = "provider",
  DATE_RANGE = "dateRange",
}

// NOTE: Wire value sent on the messages-list direction filter. Mirrors the
// backend's EMessageDirection enum without pulling server code in.
export enum EMessageDirection {
  INPUT = "input",
  OUTPUT = "output",
}

export interface IAnalyticsMessagesParams extends Omit<
  IAnalyticsQueryParams,
  "includeDeleted"
> {
  messageUuid?: string;
  limit?: number;
  offset?: number;
  sortColumn?: EAnalyticsMessageSortColumn;
  sortOrder?: TAnalyticsSortOrder;
  direction?: EMessageDirection;
  source?: string[];
  status?: string[];
  modelTitle?: string[];
  providerName?: string[];
  assistantName?: string[];
  deletionStatus?: EDeletionStatus;
  // NOTE: Free-text search over message content, matched server-side by a
  // decrypt-and-scan. PII-anonymized content is searched as stored, so terms
  // replaced by placeholders in anonymized projects won't match.
  search?: string;
}

export interface IFormatModelDisplayProps {
  isMultiModel: boolean;
  model: string | null;
  provider: string | null;
  modelNames: string | null;
  providerNames: string | null;
}

export interface IPaginatedAnalyticsMessagesResponse {
  data: IAnalyticsMessage[];
  total: number;
  hasNextPage: boolean;
  // NOTE: Set when a content search hit the scan hard cap — the page is valid
  // but incomplete, so the UI prompts for tighter filters.
  isSearchTruncated?: boolean;
}

export interface IMessageModelAndProviderOptionsResponse {
  models: string[];
  providers: string[];
  assistants: string[];
}

export interface IMessageContextParams {
  chatUuid: string;
  messageUuid: string;
  projectUuid?: string;
  includeDeleted?: boolean;
}

export type TMessageContextResponse = IMessage[];

export enum EThreadWindowDirection {
  BEFORE = "before",
  AFTER = "after",
}

// NOTE: Opaque pagination cursor for the Security Analytics thread drawer —
// the boundary turn root's createdAt (ISO) + uuid tiebreaker.
export interface IThreadWindowCursor {
  createdAt: string;
  uuid: string;
}

export interface IChatThreadWindowParams {
  chatUuid: string;
  anchorMessageUuid: string;
  projectUuid?: string;
  limit?: number;
  direction?: EThreadWindowDirection;
  cursorCreatedAt?: string;
  cursorUuid?: string;
  includeDeleted?: boolean;
}

export interface IChatThreadWindowResponse {
  messages: IMessage[];
  chatTitle: string;
  isChatDeleted: boolean;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  beforeCursor: IThreadWindowCursor | null;
  afterCursor: IThreadWindowCursor | null;
}

// NOTE: Cross-project file download from the Security Analytics Message
// Details modal. The row's projectUuid (or scope=org) tells the server which
// project owns the file so it can re-enter RLS context appropriately —
// independent of the caller's ambient X-Project-Uuid header.
export interface IDownloadAnalyticsMessageFileParams extends IAnalyticsScopeParams {
  fileUuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
}

// NOTE: Cross-project file preview from Security Analytics. Same scope
// resolution as download but hits the Gotenberg preview endpoint.
export interface IPreviewAnalyticsMessageFileParams extends IAnalyticsScopeParams {
  fileUuid: string;
}

// NOTE: NSAI-6839 — how an uploaded attachment was anonymized. Mirrors
// `EMessageFileAnonymizationStatus` in
// `apps/nsai/server/src/analytics/analytics.types.ts`.
export enum EMessageFileAnonymizationStatus {
  ANONYMIZED = "anonymized",
  NOT_MASKED = "not_masked",
  NO_TEXT_CONTENT = "no_text_content",
}

// NOTE: NSAI-6839 — which text the document-anonymization view shows: the
// anonymized text (masked values drawn as their placeholders, the form in which
// any part of the document reaches the model) or the original (restored
// values, each revealing the placeholder it travelled as).
export enum EDocumentAnonymizationViewMode {
  SENT = "sent",
  ORIGINAL = "original",
}

export interface IMessageFileAnonymizationParams extends IAnalyticsScopeParams {
  fileUuid: string;
}

// NOTE: NSAI-6839 — `content` is the RESTORED document text (what the user
// uploaded) and `piiSpans` locate the values that reached the model as
// placeholders, with the same contract as a message's spans. The server caps
// `content`; `isTruncated` says the tail was cut and `totalLength` how long the
// whole text is. `maskedValueCount` and `entityTypeCounts` cover the whole
// document, including a tail the cap left out.
export interface IMessageFileAnonymization {
  fileUuid: string;
  fileName: string;
  mimeType: string;
  status: EMessageFileAnonymizationStatus;
  content: string | null;
  piiSpans: IPiiSpan[];
  maskedValueCount: number;
  entityTypeCounts: IPiiEntityTypeCount[];
  totalLength: number;
  isTruncated: boolean;
}

// NOTE: NSAI-6895 — the slice of a thread message `isMediaGenerationTurn`
// reads; web's analytics context and thread window both pass `IMessage`.
export type TMediaGenerationTurnMessage = Pick<
  IMessage,
  "uuid" | "role" | "parentId" | "generationType"
>;

export interface IIsMediaGenerationTurnProps {
  messages: TMediaGenerationTurnMessage[] | undefined;
  messageUuid: string;
}
